import { Container, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { Offer } from '@shared/components/offers/offer-types'
import { OfferFormLotCard } from '@shared/components/offers/offer-form-lot-card/offer-form-lot-card'
import { ZenteraLogo } from '@shared/components/zentera-logo/zentera-logo'

export type OfferFormProps = {
  readOnly: boolean
  brand: string
  sentDateTime?: Date
  offer: Offer
}

/**
 * OfferForm component displays an offer for wool, including details such as brand, expiry date, payment date, and a personalized comment.

 *
 * @param readOnly - If true, disables editing of the offer lines.
 * @param brand - The name of the brand receiving the offer.
 * @param sentDateTime - The date and time the offer was sent.
 * @param offer - The data for the offer form.
 * @param offer.lines - An array of offer line items (lots) to display.
 * @param offer.personalisedComment - A custom comment included in the offer.
 * @param offer.offerFrom - The name of the person sending the offer.
 * @param offer.expiryDateTime - The date and time when the offer expires.
 * @param offer.paymentDate - The date when payment will be made if the offer is accepted.
 *
 * @returns {JSX.Element} The rendered OfferForm component.
 */
export const OfferForm = ({
  readOnly = false,
  brand,
  sentDateTime,
  offer: { lines, personalisedComment, offerFrom, expiryDateTime, paymentDate },
}: OfferFormProps) => {
  const formattedExpiryDatetime = formatDate(expiryDateTime, 'h:mm A on Do MMMM YYYY')
  const formattedPaymentDate = formatDate(paymentDate, 'Do MMMM YYYY')
  const formattedSentDateTime = formatDate(sentDateTime, 'Do MMMM YYYY')

  return (
    <Container px={0} aria-label='Offer preview'>
      <Paper withBorder p='lg'>
        <Stack>
          <Group justify='space-between'>
            <Title order={1}>
              <ZenteraLogo />
            </Title>
            <Stack gap='xs' ta='right'>
              <Text>
                Brand:{' '}
                <Text span fw={700}>
                  {brand}
                </Text>
              </Text>
              {formattedSentDateTime && (
                <Text aria-label='Offer date'>
                  Date:{' '}
                  <Text span fw={700}>
                    {formattedSentDateTime}
                  </Text>
                </Text>
              )}
            </Stack>
          </Group>

          <Text>
            We are pleased to present you the following offer on your wool. If you accept, the
            payment will be made on {formattedPaymentDate}. Prices shown are gross and in cents per
            kg.
          </Text>

          <Text>
            Please note that{' '}
            <Text span fw={700}>
              this offer expires at {formattedExpiryDatetime}
            </Text>
            .
          </Text>

          <Text>{personalisedComment}</Text>

          {lines?.map((lot, index) => (
            <OfferFormLotCard key={index} lot={lot} index={index} readOnly={readOnly} />
          ))}

          <Text>
            If you have any questions regarding this offer please contact myself or your Area
            Manager.
          </Text>

          <Text>Kind regards,</Text>

          <Text>{offerFrom}</Text>

          <Text>The Zentera Commercial Team - Sent on behalf of The Zentera Wool Company</Text>
        </Stack>
      </Paper>
    </Container>
  )
}
